/*
 * Enrolls a verified Box folder and requests its first sync straight away. Bender creates
 * the recurring schedule before it writes the registry row, so the schedule's immediate
 * first fire can miss the row; without an explicit refresh a new folder may sit unsynced
 * until the next scheduled run. Kept apart from AddBoxFolderModal so the modal stays
 * focused on input and access checking.
 */
import { useTranslation } from 'src/arb-wrapper';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { BOX_SYNC_TARGET_REFRESH_SCHEDULE, type SyncTargetLocale } from 'src/constants';
import { useCreateSyncTargetMutation, useRefreshSyncTargetMutation } from 'src/store/bender-api';
import { type CreateSyncTargetRequest, SyncTargetTypes } from 'src/types/sync-target.types';
import { normalizeBenderError } from 'src/utils/benderErrors';
import { globToRegExpSource } from 'src/utils/globPattern';
import { Logger } from 'src/utils/logger';

export interface EnrollBoxFolderArgs {
  readonly folderId: string;
  readonly folderName: string;
  readonly userAlias: string;
  readonly excludedFolderIds: readonly string[];
  /** User-facing globs; converted to the regex sources Bender stores when the request is built. */
  readonly excludedPathnames: readonly string[];
  /** `null` when the user chose no default locale; sent only when one is chosen. */
  readonly defaultLocale: SyncTargetLocale | null;
}

export interface EnrollBoxFolder {
  /** Resolves `true` when the modal should close. */
  readonly enroll: (args: EnrollBoxFolderArgs) => Promise<boolean>;
  readonly isEnrolling: boolean;
}

export const buildCreateSyncTargetRequest = ({
  folderId,
  userAlias,
  excludedFolderIds,
  excludedPathnames,
  defaultLocale,
}: EnrollBoxFolderArgs): CreateSyncTargetRequest => ({
  syncTargetType: SyncTargetTypes.BOX,
  targetId: folderId,
  refreshSchedule: BOX_SYNC_TARGET_REFRESH_SCHEDULE,
  userAlias,
  ...(excludedFolderIds.length > 0 ? { excludedFolderIds: [...excludedFolderIds] } : {}),
  ...(excludedPathnames.length > 0 ? { excludedPathnames: excludedPathnames.map(globToRegExpSource) } : {}),
  ...(defaultLocale !== null ? { defaultLocale } : {}),
});

export const useEnrollBoxFolder = (): EnrollBoxFolder => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const [createSyncTarget, { isLoading: isCreating }] = useCreateSyncTargetMutation();
  const [refreshSyncTarget, { isLoading: isRefreshing }] = useRefreshSyncTargetMutation();

  const requestFirstSync = async (syncTargetId: string | undefined): Promise<boolean> => {
    // The composite id is never built client-side, so a response without one cannot be refreshed.
    if (!syncTargetId) {
      Logger.error('Created Box folder response carried no syncTargetId; first sync not requested');
      return false;
    }
    try {
      await refreshSyncTarget({ syncTargetId }).unwrap();
      return true;
    } catch (caught) {
      const normalized = normalizeBenderError(caught);
      Logger.error(`Failed to request the first sync for ${syncTargetId}`, {
        status: normalized.status,
        detail: normalized.message,
      });
      return false;
    }
  };

  const reportCreateFailure = (folderId: string, caught: unknown): boolean => {
    const normalized = normalizeBenderError(caught);
    Logger.error(`Failed to add Box folder ${folderId}`, {
      status: normalized.status,
      detail: normalized.message,
    });

    // 409 means the folder is already enrolled; treat as success without changing its exclusions or sync state.
    if (normalized.status === 409) {
      createToast({ type: AlertType.SUCCESS, message: t('box_folder_already_added') });
      return true;
    }

    createToast({
      type: AlertType.ERROR,
      message: t(
        normalized.status === 400
          ? 'box_folder_add_rejected'
          // 502 leaves nothing enrolled and cleans up any partial schedule, so
          // retrying is safe.
          : 'box_folder_add_retry_error',
      ),
    });
    return false;
  };

  const enroll = async (args: EnrollBoxFolderArgs): Promise<boolean> => {
    let syncTargetId: string | undefined;
    try {
      const created = await createSyncTarget(buildCreateSyncTargetRequest(args)).unwrap();
      syncTargetId = created?.syncTargetId;
    } catch (caught) {
      return reportCreateFailure(args.folderId, caught);
    }

    const isSyncRequested = await requestFirstSync(syncTargetId);
    // One toast either way: ToasterProvider builds each toast from the last rendered list,
    // so two created in the same tick would drop the first.
    createToast({
      type: AlertType.SUCCESS,
      message: t(isSyncRequested ? 'box_folder_added' : 'box_folder_added_sync_pending', { name: args.folderName }),
    });
    return true;
  };

  return { enroll, isEnrolling: isCreating || isRefreshing };
};
